import Bugsnag from '@bugsnag/js'
import { Dispatch } from 'react'
import { useRouter } from 'next/router'

import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import {
  IAction,
  IErrorAction,
  IOrderSuccessAction,
  IPartialOrderSuccessAction,
} from 'checkout/containers/CheckoutStateReducer'
import { Order } from 'shared/types/Order'
import { convertToDollars } from 'shared/lib/formatCurrency'
import { createOrderBodyPayload } from 'checkout/utils/createOrderBodyPayload'
import { isTimeoutError, timeoutMessage } from 'checkout/utils/timeout'
import { logEcomPurchase } from 'mParticle/ecommerceMParticleEvents'
import { selectIsFastestToYouAvailable } from 'store/checkoutSlice'
import { t } from 'localization'
import { useAppSelector } from 'store/hooks'
import { useAuth } from 'shared/hooks/useAuth'
import { useBuyOrderMutation, useCreateOrderMutation } from 'api/ordersApi'
import { useLazyGetPriceBreakdownQuery } from 'api/productVariantsApi'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { useUser } from 'shared/hooks/useUser'
import { useTikTokPixelTrackEvent } from 'head/hooks/useTikTokPixelTrackEvent'

const TP = 'checkout.hooks.useOrderRTK'

type UseOrderProps = {
  dispatch: Dispatch<IAction | IOrderSuccessAction | IPartialOrderSuccessAction | IErrorAction>
  order?: Order
}

export const useOrderRTK = ({ dispatch, order: orderFromState }: UseOrderProps) => {
  const router = useRouter()
  const { logoutUser } = useAuth()
  const { currentUser, isAuthenticated } = useUser()
  const { country, currencyCode } = useShoppingRegionContext()
  const isFastestToYouAvailable = useAppSelector(selectIsFastestToYouAvailable)
  const { trackTikTokCheckoutPlaceOrder } = useTikTokPixelTrackEvent()
  const [buyOrder] = useBuyOrderMutation()
  const [createOrderMutation] = useCreateOrderMutation()
  const [getPriceBreakdownQuery] = useLazyGetPriceBreakdownQuery()

  const {
    boxCondition,
    condition,
    details,
    expectedPriceCents,
    instantShip,
    localizedExpectedPriceCents,
    mainPictureUrl,
    name,
    productCategory,
    productId,
    productTemplateId,
    size,
    sizeDisplay,
  } = router.query
  const priceBreakdownBoxCondition = orderFromState?.product?.boxCondition || boxCondition
  const priceBreakdownInstantShip = orderFromState?.isInstantShip || instantShip === 'true'
  const priceBreakdownPriceCents = Number(
    orderFromState?.localizedPriceCents?.amount ||
      localizedExpectedPriceCents ||
      expectedPriceCents,
  )
  const priceBreakdownProductCategory =
    orderFromState?.product?.productTemplate?.productCategory || productCategory
  const priceBreakdownProductId = orderFromState?.product?.id || productId
  const priceBreakdownProductTemplateId =
    orderFromState?.product?.productTemplate?.id || productTemplateId
  const priceBreakdownShoeCondition = orderFromState?.product?.shoeCondition || condition
  const priceBreakdownSize = orderFromState?.product?.sizeOption?.presentation || size

  const fallbackErrorMessage = t(
    `${TP}.requestFailure`,
    'Something went wrong.  Please try again or reach out to support@flightclub.com',
  )

  const trackError = (methodName: string, error: Error) => {
    const notifyError = new Error(error?.data?.messages?.[0] || error?.message || methodName)
    Bugsnag.notify(notifyError, (event) => {
      event.addMetadata('checkout', {
        addressId: orderFromState?.addressId,
        billingInfoId: orderFromState?.billingInfoId,
        id: orderFromState?.id,
        isInstantShip: priceBreakdownInstantShip,
        localizedCreditCents: orderFromState?.localizedCreditCents,
        localizedFinalPriceCents: orderFromState?.localizedFinalPriceCents,
        localizedPromoCodeValueCents: orderFromState?.localizedPromoCodeValueCents,
        localizedShippingCents: orderFromState?.localizedShippingCents,
        orderNumber: orderFromState?.number,
        paymentType: orderFromState?.paymentType,
        shippingAddressInRegion: orderFromState?.shippingAddressInRegion,
        shippingServiceLevel: orderFromState?.shippingServiceLevel,
        status: orderFromState?.status,
      })
      event.addMetadata('error', {
        dataMessages: error?.data?.messages,
        file: 'useOrderRTK',
        message: error?.message,
        method: methodName,
        status: error?.status,
      })
      event.addMetadata('product', {
        boxCondition: priceBreakdownBoxCondition,
        consigned: orderFromState?.product?.consigned,
        id: priceBreakdownProductTemplateId || priceBreakdownProductId,
        name: orderFromState?.product?.productTemplate?.name || name,
        saleStatus: orderFromState?.product?.saleStatus,
        shoeCondition: priceBreakdownShoeCondition,
        size: priceBreakdownSize,
        slug: orderFromState?.product?.slug || orderFromState?.product?.productTemplate?.slug,
        variantShoeCondition: orderFromState?.product?.variantShoeCondition,
      })
      event.addMetadata('user', {
        id: currentUser?.id,
        isGuest: currentUser?.isGuest,
      })
      event.context = 'checkout'
      event.severity = 'error'
    })
  }

  const buyOrderRTK = async () => {
    try {
      if (
        !orderFromState?.number ||
        Number.isNaN(orderFromState?.localizedFinalPriceCents?.amountUsdCents)
      ) {
        throw new Error()
      }
      dispatch({ type: 'purchaseOrderRequest' })
      const buyOrderBody = {
        orderNumber: orderFromState.number,
        // NOTE: Regardless of currency, backend expects finalPriceCents in USD when completing purchase
        finalPriceCents: orderFromState.localizedFinalPriceCents.amountUsdCents,
      }
      await buyOrder(buyOrderBody).unwrap()

      sendTrackingEvent('CART_PURCHASE', {
        currency_code: currencyCode,
        fastest_to_you: orderFromState?.isInstantShip,
        is_fastest_to_you_available: isFastestToYouAvailable,
        is_guest: !!currentUser?.isGuest,
        order_number: orderFromState?.number,
        price: `${orderFromState?.localizedPriceCents?.amountUsdCents || ''}`,
        product_template_slug: orderFromState?.product?.productTemplate?.slug,
        request_type: 'rtk',
        subtotal_price_dollars: `${
          convertToDollars(orderFromState?.localizedPriceCents?.amountUsdCents) || ''
        }`,
        utm_campaign: window.sessionStorage.getItem('utm_campaign') || '',
        utm_medium: window.sessionStorage.getItem('utm_medium') || '',
        utm_source: window.sessionStorage.getItem('utm_source') || '',
      })

      logEcomPurchase(
        {
          brandName: orderFromState?.product?.productTemplate?.brandName,
          condition: orderFromState?.product?.shoeCondition,
          name: orderFromState?.product?.productTemplate?.name,
          priceCents: orderFromState?.localizedPriceCents?.amount,
          sku: orderFromState?.product?.productTemplate?.sku,
        },
        {
          number: orderFromState?.number,
          priceCents: orderFromState?.localizedFinalPriceCents?.amountUsdCents,
        },
      )

      trackTikTokCheckoutPlaceOrder({
        contentCategory: orderFromState?.product?.productTemplate?.brandName,
        contentId: orderFromState?.product?.productTemplate?.sku,
        price: orderFromState?.localizedPriceCents?.amount,
        quantity: 1,
        value: orderFromState?.localizedFinalPriceCents?.amount,
      })

      router.push(
        `/checkout/${orderFromState?.number}/success`,
        `/checkout/${orderFromState?.number}/success`,
        { locale: router.locale },
      )
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (isTimeoutError(error)) {
        dispatch({ type: 'purchaseOrderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      trackError('buyOrderRTK', error)
      dispatch({
        type: 'purchaseOrderRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const changeOrderOptionRTK = async ({ expectedPriceCents, instantShip }) => {
    dispatch({ type: 'orderRequest' })
    if (isAuthenticated) {
      try {
        const shoeCondition = orderFromState?.product?.variantShoeCondition
        // Create a new order with the proper Lowest Price/F2Y option
        const payload = {
          boxCondition: orderFromState?.product?.boxCondition,
          condition: shoeCondition,
          expectedPriceCents,
          instantShip: instantShip ? 'true' : 'false',
          productId: shoeCondition === 'new_no_defects' ? null : orderFromState?.product?.id,
          productTemplateId:
            shoeCondition === 'new_no_defects'
              ? orderFromState?.product?.productTemplate?.id
              : null,
          size: orderFromState?.product?.sizeOption?.presentation,
        }
        const body = createOrderBodyPayload(payload)
        const data = await createOrderMutation(body).unwrap()
        await router.replace(`/checkout?orderNumber=${data.number}`, `/checkout/${data.number}`, {
          locale: router.locale,
        })
        dispatch({ type: 'orderRequestSuccess', payload: data })
        return
      } catch (error) {
        const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
        if (isTimeoutError(error)) {
          dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
          await logoutUser()
          return
        }
        trackError('changeOrderOptionRTK', error)
        dispatch({
          type: 'orderRequestFailure',
          payload: errorMessage || error?.message || fallbackErrorMessage,
        })
        return
      }
    }
    try {
      await router.replace({
        query: {
          ...router.query,
          expectedPriceCents,
          instantShip: instantShip ? 'true' : 'false',
          localizedExpectedPriceCents: expectedPriceCents,
        },
      })
      const priceBreakdown = await getPriceBreakdownQuery({
        boxCondition: priceBreakdownBoxCondition || '',
        countryCode: country || 'US',
        isInstantShip: instantShip,
        priceCents: expectedPriceCents || '',
        productId: priceBreakdownProductId || '',
        productTemplateId: priceBreakdownProductTemplateId || '',
        shoeCondition: priceBreakdownShoeCondition || '',
        size: priceBreakdownSize || '',
      }).unwrap()
      const priceBreakdownUpdate = {
        applicableCostsNote: priceBreakdown?.applicableCostsNote,
        isInstantShip: instantShip,
        localizedInstantShipMarkupFeeCents: priceBreakdown?.localizedMarkupFeeCents,
        localizedListPriceCents: priceBreakdown?.localizedBasePriceCents,
        priceBreakdownType: priceBreakdown?.priceBreakdownType,
        pricingBreakdown: priceBreakdown?.pricingBreakdown,
      }
      dispatch({
        type: 'priceEstimateRequestSuccess',
        payload: priceBreakdownUpdate,
      })
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      trackError('changeOrderOptionRTK', error)
      dispatch({
        type: 'orderRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const createOrderRTK = async () => {
    if (orderFromState?.number) {
      return
    }
    dispatch({ type: 'orderRequest' })
    try {
      const body = createOrderBodyPayload(router.query)
      const data = await createOrderMutation(body).unwrap()
      await router.replace(`/checkout?orderNumber=${data.number}`, `/checkout/${data.number}`, {
        locale: router.locale,
      })
      dispatch({ type: 'orderRequestSuccess', payload: data })
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      trackError('createOrderRTK', error)
      dispatch({
        type: 'orderRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const getPriceBreakdownRTK = async () => {
    dispatch({ type: 'orderRequest' })
    const priceBreakdown = await (async () => {
      if (priceBreakdownPriceCents && (productId || productTemplateId)) {
        try {
          return await getPriceBreakdownQuery({
            boxCondition: priceBreakdownBoxCondition || '',
            countryCode: country || 'US',
            isInstantShip: priceBreakdownInstantShip,
            priceCents: priceBreakdownPriceCents || '',
            productId: priceBreakdownProductId || '',
            productTemplateId: priceBreakdownProductTemplateId || '',
            shoeCondition: priceBreakdownShoeCondition || '',
            size: priceBreakdownSize || '',
          }).unwrap()
        } catch (error) {
          const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
          if (isTimeoutError(error)) {
            dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
            await logoutUser()
            return null
          }
          trackError('getPriceBreakdownRTK', error)
          dispatch({
            type: 'orderRequestFailure',
            payload: errorMessage || error?.message || fallbackErrorMessage,
          })
          return null
        }
      }

      return null
    })()

    if (productId || productTemplateId) {
      // If id is coming from query params, create fake order for OrderSummary details
      const zeroPrice = { amount: 0, currency: currencyCode }
      const productPrice = { amount: priceBreakdownPriceCents, currency: currencyCode }
      const partialOrder = {
        applicableCostsNote: priceBreakdown?.applicableCostsNote,
        isInstantShip: instantShip === 'true',
        localizedFinalPriceCents: productPrice,
        localizedInstantShipMarkupFeeCents: priceBreakdown?.localizedMarkupFeeCents || zeroPrice,
        localizedListPriceCents: priceBreakdown?.localizedBasePriceCents || productPrice,
        localizedPriceCents: productPrice,
        localizedShippingCents: zeroPrice,
        localizedTaxCents: zeroPrice,
        priceBreakdownType: priceBreakdown?.priceBreakdownType,
        pricingBreakdown: priceBreakdown?.pricingBreakdown,
        product: {
          id: Number(productId),
          mainPictureUrl: String(mainPictureUrl),
          productTemplate: {
            details: String(details),
            id: Number(productTemplateId),
            name: String(name),
            productCategory: priceBreakdownProductCategory,
          },
          variantShoeCondition: priceBreakdownShoeCondition,
          sizeOption: { presentation: String(sizeDisplay), value: Number(size) },
        },
      }
      dispatch({
        type: 'priceEstimateRequestSuccess',
        payload: partialOrder,
      })
    } else {
      // Else, order already exists. Update localizedInstantShipMarkupFeeCents & localizedListPriceCents
      const priceBreakdownUpdate = {
        applicableCostsNote: priceBreakdown?.applicableCostsNote,
        localizedInstantShipMarkupFeeCents: priceBreakdown?.localizedMarkupFeeCents,
        localizedListPriceCents: priceBreakdown?.localizedBasePriceCents,
        priceBreakdownType: priceBreakdown?.priceBreakdownType,
        pricingBreakdown: priceBreakdown?.pricingBreakdown,
      }
      dispatch({
        type: 'priceEstimateRequestSuccess',
        payload: priceBreakdownUpdate,
      })
    }
  }

  return { buyOrderRTK, changeOrderOptionRTK, createOrderRTK, getPriceBreakdownRTK }
}
